/**
 * single-product.js — the product page
 *
 * Progressive enhancement, all of it. With this script blocked the page still
 * works: every tab panel is in the document and the tab bar is a set of jump
 * links, the gallery shows its main photo, the variation controls fall back to
 * WooCommerce's own <select>s, and the video is a poster with a link.
 *
 * Enqueued from woocommerce/single-product.php with:
 *   whytheme_enqueue_script('woocommerce/single-product');
 */

/* -------------------------------------------------------------------------- */
/* Tabs                                                                       */
/* -------------------------------------------------------------------------- */

function initTabs() {
    const bar = document.querySelector('[data-samba-tabs]');

    if (!bar) return;

    const tabs = [...bar.querySelectorAll('[data-samba-tab]')];
    const panels = [...document.querySelectorAll('[data-samba-tabpanel]')];

    if (tabs.length < 2) return;

    const show = (id, { focus = false, push = false } = {}) => {
        let matched = false;

        tabs.forEach((tab) => {
            const on = tab.dataset.sambaTab === id;
            matched = matched || on;

            tab.dataset.state = on ? 'on' : 'off';
            tab.setAttribute('aria-selected', String(on));
            // Roving tabindex: only the selected tab is in the tab order.
            tab.tabIndex = on ? 0 : -1;
            tab.querySelector('[data-samba-tab-underline]')?.classList.toggle('opacity-0', !on);
        });

        if (!matched) return false;

        panels.forEach((panel) => {
            const on = panel.dataset.sambaTabpanel === id;
            panel.dataset.state = on ? 'on' : 'off';
            panel.hidden = !on;
        });

        if (focus) {
            tabs.find((tab) => tab.dataset.sambaTab === id)?.focus();
        }

        if (push && window.history?.replaceState) {
            window.history.replaceState(null, '', `#samba-product-tab-${id}`);
        }

        return true;
    };

    tabs.forEach((tab) => {
        tab.addEventListener('click', (event) => {
            event.preventDefault();
            show(tab.dataset.sambaTab, { push: true });
        });

        tab.addEventListener('keydown', (event) => {
            const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 1, ArrowUp: -1 }[event.key];

            if (!step) return;

            event.preventDefault();
            const next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
            show(next.dataset.sambaTab, { focus: true, push: true });
        });
    });

    // Anything else on the page that points at a tab, e.g. the rating link.
    document.querySelectorAll('[data-samba-tab-link]').forEach((link) => {
        link.addEventListener('click', (event) => {
            if (!show(link.dataset.sambaTabLink, { push: true })) return;

            event.preventDefault();
            bar.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    });

    // Deep link: /product/x/#samba-product-tab-reviews opens that tab.
    const fromHash = window.location.hash.replace('#samba-product-tab-', '');

    if (!fromHash || !show(fromHash)) {
        show(tabs[0].dataset.sambaTab);
    }
}

/* -------------------------------------------------------------------------- */
/* Gallery                                                                    */
/* -------------------------------------------------------------------------- */

function initGallery() {
    const root = document.querySelector('[data-samba-gallery]');
    const main = root?.querySelector('[data-samba-gallery-main]');
    const thumbs = [...(root?.querySelectorAll('[data-samba-gallery-thumb]') || [])];

    if (!main || thumbs.length < 2) return;

    const select = (button) => {
        thumbs.forEach((thumb) => {
            const on = thumb === button;
            thumb.dataset.state = on ? 'on' : 'off';
            thumb.setAttribute('aria-pressed', String(on));
            thumb.classList.toggle('border-samba-stroke', !on);
        });

        main.src = button.dataset.full;
        main.srcset = button.dataset.srcset || '';

        if (button.dataset.alt) {
            main.alt = button.dataset.alt;
        }
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => select(thumb)));
}

/* -------------------------------------------------------------------------- */
/* Variation option buttons                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The buttons are a face for WooCommerce's own <select> elements — they never
 * hold state of their own. Clicking one writes to the select and fires a
 * native change event, which is what wc-add-to-cart-variation.js listens for;
 * reading back from the select afterwards is how the buttons learn which
 * combinations are still available.
 */
function initVariations() {
    const groups = [...document.querySelectorAll('[data-samba-option-group]')];

    if (!groups.length) return;

    const form = document.querySelector('.variations_form');

    const wired = groups
        .map((group) => {
            const select = group.querySelector('select');
            const fallback = group.querySelector('[data-samba-option-fallback]');
            const buttons = group.querySelector('[data-samba-option-buttons]');

            if (!select || !buttons) return null;

            return { group, select, fallback, buttons, options: [...buttons.querySelectorAll('[data-samba-option]')] };
        })
        .filter(Boolean);

    if (!wired.length) return;

    const sync = () => {
        wired.forEach(({ select, options }) => {
            // WooCommerce rebuilds the option list whenever another attribute
            // changes, so availability is read from the select every time.
            const available = new Map(
                [...select.options]
                    .filter((option) => option.value !== '')
                    .map((option) => [option.value, !option.disabled])
            );

            options.forEach((button) => {
                const value = button.dataset.value;
                const on = select.value === value;

                button.dataset.state = on ? 'on' : 'off';
                button.setAttribute('aria-checked', String(on));
                button.disabled = available.has(value) ? !available.get(value) : true;
            });
        });
    };

    wired.forEach(({ select, fallback, buttons, options }) => {
        // Hand over from the select to the buttons.
        if (fallback) fallback.hidden = true;
        buttons.hidden = false;

        options.forEach((button) => {
            button.addEventListener('click', () => {
                // Clicking the chosen one again clears it, which is how you
                // get back to WooCommerce's "Choose an option" state.
                select.value = select.value === button.dataset.value ? '' : button.dataset.value;
                select.dispatchEvent(new Event('change', { bubbles: true }));
                sync();
            });
        });

        select.addEventListener('change', sync);
    });

    if (form) {
        // WooCommerce's own events, fired after it has recalculated which
        // combinations are still possible.
        ['woocommerce_update_variation_values', 'show_variation', 'hide_variation', 'reset_data'].forEach((name) => {
            form.addEventListener(name, sync);
        });

        if (window.jQuery) {
            window.jQuery(form).on(
                'woocommerce_update_variation_values show_variation hide_variation reset_data',
                sync
            );
        }
    }

    sync();
}

/* -------------------------------------------------------------------------- */
/* Price in the add-to-cart button                                            */
/* -------------------------------------------------------------------------- */

/**
 * The maquette prints one price per page — in the panel and again inside the
 * CTA ("ADD TO CART | €22,00"). For a variable product that price only exists
 * once a variation is picked, so both are mirrored from whatever WooCommerce
 * has just rendered into .woocommerce-variation-price, and both fall back to
 * the price range the page was served with.
 */
function initCtaPrice() {
    const form = document.querySelector('.variations_form');
    const target = form?.querySelector('[data-samba-cta-price]');

    if (!form || !target) return;

    const separator = form.querySelector('[data-samba-cta-separator]');
    const panelPrice = document.querySelector('[data-samba-panel-price]');
    const range = panelPrice?.innerHTML || '';

    const update = () => {
        const price = form.querySelector('.woocommerce-variation-price');
        const html = price?.innerHTML?.trim() || '';
        // A sale shows both prices; the one being charged is the <ins>.
        const current = price?.querySelector('ins') || price;
        const text = current?.textContent?.trim() || '';

        target.textContent = text;

        if (separator) separator.hidden = text === '';

        // Back to the range when the selection is cleared.
        if (panelPrice) panelPrice.innerHTML = html === '' ? range : html;
    };

    ['show_variation', 'hide_variation', 'reset_data'].forEach((name) => {
        form.addEventListener(name, update);
    });

    if (window.jQuery) {
        window.jQuery(form).on('show_variation hide_variation reset_data', update);
    }

    update();
}

/* -------------------------------------------------------------------------- */
/* Video                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The provider's iframe is only inserted on click, so nothing is requested
 * from YouTube or Vimeo — and no cookie is set — until someone asks for it.
 */
function initVideo() {
    const player = document.querySelector('[data-samba-video]');
    const button = player?.querySelector('[data-samba-video-play]');

    if (!player || !button || !player.dataset.embed) return;

    button.addEventListener('click', () => {
        player.innerHTML = player.dataset.embed;

        const frame = player.querySelector('iframe');

        if (frame) {
            frame.classList.add('absolute', 'inset-0', 'size-full');
            frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
            frame.setAttribute('allowfullscreen', '');

            // Autoplay, since the visitor has just pressed play.
            const join = frame.src.includes('?') ? '&' : '?';
            frame.src = `${frame.src}${join}autoplay=1`;
        }
    });
}

/* -------------------------------------------------------------------------- */
/* Share                                                                      */
/* -------------------------------------------------------------------------- */

function initShare() {
    document.querySelectorAll('[data-samba-share]').forEach((button) => {
        button.addEventListener('click', async () => {
            const { url, title, copied } = button.dataset;

            if (navigator.share) {
                try {
                    await navigator.share({ title, url });
                    return;
                } catch (error) {
                    // Cancelled, or the sheet refused — fall through to copying.
                    if (error?.name === 'AbortError') return;
                }
            }

            try {
                await navigator.clipboard.writeText(url);
                announce(button, copied);
            } catch {
                window.prompt(copied, url);
            }
        });
    });
}

/** Say something once, to screen readers and on screen, then clean up. */
function announce(anchor, message) {
    if (!message) return;

    const note = document.createElement('span');
    note.setAttribute('role', 'status');
    note.className =
        'pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-sm bg-samba-black px-2 py-1 font-body text-xs text-white';
    note.textContent = message;

    anchor.style.position = 'relative';
    anchor.appendChild(note);

    window.setTimeout(() => note.remove(), 2000);
}

/* -------------------------------------------------------------------------- */
/* "Helpful" votes on reviews                                                 */
/* -------------------------------------------------------------------------- */

function initHelpful() {
    const config = window.whythemeProduct;

    if (!config?.ajaxUrl) return;

    document.querySelectorAll('[data-samba-helpful]').forEach((button) => {
        button.addEventListener('click', async () => {
            button.disabled = true;

            const body = new URLSearchParams({
                action: 'whytheme_review_helpful',
                nonce: config.nonce,
                comment_id: button.dataset.sambaHelpful,
            });

            try {
                const response = await fetch(config.ajaxUrl, { method: 'POST', body, credentials: 'same-origin' });
                const result = await response.json();
                const count = result?.data?.count;

                if (typeof count !== 'undefined') {
                    const slot = button.querySelector('[data-samba-helpful-count]');
                    if (slot) slot.textContent = count;
                }

                button.classList.add('opacity-60');
            } catch {
                button.disabled = false;
            }
        });
    });
}

/* -------------------------------------------------------------------------- */

function init() {
    initTabs();
    initGallery();
    initVariations();
    initCtaPrice();
    initVideo();
    initShare();
    initHelpful();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
